import { useEffect, useCallback, useMemo, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import Shepherd from "shepherd.js";
import { createAppOverviewTour } from "@/tours/appOverviewTour";
import { useAnalytics } from "@/hooks/useAnalytics";
import { apiClient } from "@/services/api";
import { UserData } from "@/types/user";
import {
  waitForTourElements,
  hasCompletedTourInSession,
  markTourCompletedInSession,
} from "@/utils/tourUtils";

const STORAGE_KEY = "app_tour_completed";

// Required tour target elements (at minimum, the first step element must exist)
const REQUIRED_TOUR_ELEMENTS = ['[data-tour="sidebar-logo"]'];

/**
 * useAppTour Hook
 *
 * Manages the app overview tour lifecycle:
 * - Checks if tour has been completed (localStorage + session-based)
 * - Waits for DOM elements to be ready before starting tours
 * - Offers to start tour for authenticated users who haven't completed it
 * - Tracks tour events to PostHog
 * - Persists completion status to localStorage and sessionStorage
 */
export function useAppTour() {
  const { isAuthenticated } = useAuth0();
  const { track, posthog } = useAnalytics();
  const queryClient = useQueryClient();

  const tourRef = useRef<InstanceType<typeof Shepherd.Tour> | null>(null);
  const isStartingRef = useRef(false);

  // Fetch user data to check backend tour completion status
  const { data: userData, isLoading: isUserDataLoading } = useQuery<UserData>({
    queryKey: ["user", "me"],
    queryFn: async () => {
      const data = await apiClient.get<UserData>("/api/v1/user/me/");
      return data;
    },
    enabled: isAuthenticated,
    staleTime: 30000, // 30 seconds
  });

  const hasCompletedTour = useMemo(() => {
    // Priority 1: Check backend (user profile) - most reliable, survives cache clearing
    if (userData?.app_tour_completed === true) {
      return true;
    }

    // Priority 2: Check localStorage (fallback for offline/unauthenticated)
    const localStorageCompleted = localStorage.getItem(STORAGE_KEY) === "true";

    // Priority 3: Check sessionStorage (prevents repeats in same session)
    const sessionCompleted = hasCompletedTourInSession(STORAGE_KEY);

    return localStorageCompleted || sessionCompleted;
  }, [userData?.app_tour_completed]);

  const markTourCompleted = useCallback(async () => {
    // Mark in localStorage (fallback)
    localStorage.setItem(STORAGE_KEY, "true");
    markTourCompletedInSession(STORAGE_KEY);

    // Update PostHog
    if (posthog) {
      posthog.people.set({ app_tour_completed: true });
    }

    // Save to backend (persists across cache clearing)
    if (isAuthenticated && userData?.username) {
      try {
        await apiClient.patch(`/api/v1/user/${userData.username}`, {
          app_tour_completed: true,
        });
        // Invalidate user query to refresh data
        queryClient.invalidateQueries({ queryKey: ["user", "me"] });
      } catch (error) {
        // Log error but don't block - localStorage fallback is sufficient
        console.warn("Failed to save tour completion to backend:", error);
      }
    }
  }, [posthog, isAuthenticated, userData?.username, queryClient]);

  const startTour = useCallback(async () => {
    // Prevent multiple concurrent tour starts
    if (hasCompletedTour || isStartingRef.current || tourRef.current?.isActive()) {
      return;
    }

    isStartingRef.current = true;

    try {
      // Wait for required tour elements to be available in the DOM
      await waitForTourElements(REQUIRED_TOUR_ELEMENTS, {
        maxWaitMs: 5000,
        initialDelayMs: 100,
        maxRetries: 10,
      });
    } catch (error) {
      // Log error but don't block tour - Shepherd's showOn() will handle missing elements
      console.warn("Some tour elements not ready, but starting tour anyway:", error);
    }

    if (tourRef.current?.isActive()) {
      isStartingRef.current = false;
      return;
    }

    const tour = createAppOverviewTour();
    tourRef.current = tour;

    // Track tour start
    track({
      name: "tour_started" as any,
      properties: { tour_name: "app_overview" },
    });

    // Track step views
    tour.on("show", () => {
      const step = tour.getCurrentStep();
      if (step) {
        track({
          name: "tour_step_viewed" as any,
          properties: {
            tour_name: "app_overview",
            step: step.id,
          },
        });
      }
    });

    // Track completion
    tour.on("complete", () => {
      tourRef.current = null;
      isStartingRef.current = false;
      markTourCompleted();
      track({
        name: "tour_completed" as any,
        properties: { tour_name: "app_overview" },
      });
    });

    // Track cancellation
    tour.on("cancel", () => {
      tourRef.current = null;
      isStartingRef.current = false;
      track({
        name: "tour_skipped" as any,
        properties: { tour_name: "app_overview" },
      });
    });

    tour.start();
  }, [hasCompletedTour, markTourCompleted, track]);

  const restartTour = useCallback(async () => {
    if (tourRef.current) {
      tourRef.current.cancel();
      tourRef.current = null;
    }
    isStartingRef.current = true;

    // Clear completion status (localStorage and sessionStorage)
    localStorage.removeItem(STORAGE_KEY);
    sessionStorage.removeItem("tour_session_id");

    // Update PostHog
    if (posthog) {
      posthog.people.set({ app_tour_completed: false });
    }

    // Clear backend flag (persists across cache clearing)
    if (isAuthenticated && userData?.username) {
      try {
        await apiClient.patch(`/api/v1/user/${userData.username}`, {
          app_tour_completed: false,
        });
        // Invalidate user query to refresh data
        queryClient.invalidateQueries({ queryKey: ["user", "me"] });
      } catch (error) {
        // Log error but don't block - tour can still restart
        console.warn("Failed to clear tour completion in backend:", error);
      }
    }

    try {
      // Wait for required tour elements to be available in the DOM
      await waitForTourElements(REQUIRED_TOUR_ELEMENTS, {
        maxWaitMs: 5000,
        initialDelayMs: 100,
        maxRetries: 10,
      });
    } catch (error) {
      // Log error but don't block tour - Shepherd's showOn() will handle missing elements
      console.warn("Some tour elements not ready, but starting tour anyway:", error);
    }

    // Create and start new tour directly (bypassing hasCompletedTour check)
    const tour = createAppOverviewTour();
    tourRef.current = tour;

    // Track tour start
    track({
      name: "tour_started" as any,
      properties: { tour_name: "app_overview" },
    });

    // Track step views
    tour.on("show", () => {
      const step = tour.getCurrentStep();
      if (step) {
        track({
          name: "tour_step_viewed" as any,
          properties: {
            tour_name: "app_overview",
            step: step.id,
          },
        });
      }
    });

    // Track completion
    tour.on("complete", () => {
      tourRef.current = null;
      isStartingRef.current = false;
      markTourCompleted();
      track({
        name: "tour_completed" as any,
        properties: { tour_name: "app_overview" },
      });
    });

    // Track cancellation
    tour.on("cancel", () => {
      tourRef.current = null;
      isStartingRef.current = false;
      track({
        name: "tour_skipped" as any,
        properties: { tour_name: "app_overview" },
      });
    });

    tour.start();
  }, [posthog, track, markTourCompleted, isAuthenticated, userData?.username, queryClient]);

  useEffect(() => {
    if (isUserDataLoading) {
      return;
    }

    // Auto-start tour for authenticated users who haven't completed it
    if (isAuthenticated && !hasCompletedTour) {
      // Start tour asynchronously - waitForTourElements handles timing
      startTour().catch((error) => {
        console.error("Failed to start app tour:", error);
      });
    }
  }, [isAuthenticated, hasCompletedTour, startTour, isUserDataLoading]);

  return {
    hasCompletedTour,
    startTour,
    restartTour,
  };
}
